import { z } from 'zod';

export const PDF_REPORT_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/svg+xml',
] as const;

export type PdfReportImageMimeType =
  (typeof PDF_REPORT_IMAGE_MIME_TYPES)[number];

export const resolvePdfReportPageFooterText = ({
  vendorName,
  footerLabel,
}: {
  vendorName: string;
  footerLabel?: string | null;
}): string => {
  const customFooter = footerLabel?.trim();

  if (customFooter) {
    return customFooter;
  }

  return `© ${new Date().getFullYear()} ${vendorName}. All rights reserved.`;
};

export const PDF_REPORT_COVER_TEXT_COLORS = ['light', 'dark'] as const;

export type PdfReportCoverTextColor =
  (typeof PDF_REPORT_COVER_TEXT_COLORS)[number];

export const PdfReportCoverTextColorSchema = z.enum(
  PDF_REPORT_COVER_TEXT_COLORS,
);

export const DEFAULT_PDF_REPORT_COVER_TEXT_COLOR =
  'light' satisfies PdfReportCoverTextColor;

const IMAGE_DATA_URL_PREFIX =
  /^data:image\/(jpeg|jpg|png|webp|svg\+xml);base64,/i;

const ImageBase64Schema = z
  .string()
  .min(1)
  .refine((value) => {
    const normalized = value.replace(IMAGE_DATA_URL_PREFIX, '');

    return /^[A-Za-z0-9+/]+=*$/.test(normalized);
  }, 'Invalid image base64 payload');

const OptionalFooterLabelSchema = z
  .union([z.string().trim().min(1).max(500), z.literal(''), z.null()])
  .transform((value) => (value && value.length > 0 ? value : null));

export const PdfReportBrandingSettingsSchema = z.object({
  vendorName: z.string().trim().min(1).max(200),
  footerLabel: z.union([z.string().trim().min(1).max(500), z.null()]),
  coverTextColor: PdfReportCoverTextColorSchema,
  enabled: z.boolean().default(true),
});

export const UpdatePdfReportBrandingSettingsSchema = z.object({
  vendorName: z.string().trim().min(1).max(200).optional(),
  footerLabel: OptionalFooterLabelSchema.optional(),
  coverTextColor: PdfReportCoverTextColorSchema.optional(),
  enabled: z.boolean().optional(),
});

export const PdfReportBrandingSchema = z.object({
  settings: PdfReportBrandingSettingsSchema,
  logoImageBase64: z.string().nullable(),
  coverImageBase64: z.string().nullable(),
  inheritedFromParent: z.boolean(),
});

export const PdfReportBrandingResponseSchema = z.object({
  branding: PdfReportBrandingSchema.nullable(),
  canEdit: z.boolean(),
});

const hasDefinedSettingsField = (
  settings: z.infer<typeof UpdatePdfReportBrandingSettingsSchema>,
): boolean =>
  settings.vendorName !== undefined ||
  settings.footerLabel !== undefined ||
  settings.coverTextColor !== undefined ||
  settings.enabled !== undefined;

export const UpdatePdfReportBrandingSchema = z
  .object({
    settings: UpdatePdfReportBrandingSettingsSchema.optional(),
    logoImageBase64: ImageBase64Schema.nullable().optional(),
    coverImageBase64: ImageBase64Schema.nullable().optional(),
    clearLogo: z.boolean().optional(),
    clearCover: z.boolean().optional(),
  })
  .refine(
    (value) =>
      (value.settings !== undefined &&
        hasDefinedSettingsField(value.settings)) ||
      value.logoImageBase64 !== undefined ||
      value.coverImageBase64 !== undefined ||
      Boolean(value.clearLogo) ||
      Boolean(value.clearCover),
    {
      message: 'At least one field must be provided',
    },
  );
